import { Button, Dropdown, Modal, SelectDropdownOption } from '@/components';
import { Role } from '@/modules/Users/domain/role';
import { RoleEntity } from '@/modules/Users/types/role';
import { getDropdownArray } from '@/utils/get-dropdown-array.util';
import {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface AttachUserToEntityModalProps {
  entityType: 'businessArea' | 'process';
  isOpen: boolean;
  isSubmitting: boolean;
  isConfirmDisabled: boolean;
  onHandleClose: () => void;
  onHandleConfirm: (roleId: number) => void;
  roles?: Role[];
  roleEntity?: RoleEntity;
}

export const AttachUserToEntityModal = ({
  entityType,
  isOpen,
  onHandleClose,
  onHandleConfirm,
  children,
  isSubmitting,
  isConfirmDisabled,
  roles,
  roleEntity,
}: PropsWithChildren<AttachUserToEntityModalProps>): ReactElement => {
  const { t } = useTranslation();
  const [dropdownRoleValue, setDropdownRoleValue] = useState<number>();
  const roleDropdownOptions = useMemo(
    () =>
      getDropdownArray(
        roles?.map((role) => ({
          id: role.id,
          name: t(`roles.${role.name}`, {
            entity: roleEntity && t(roleEntity),
          }),
        })) ?? [],
      ),
    [roleEntity, roles, t],
  );
  const handleConfirm = () => {
    dropdownRoleValue && onHandleConfirm(dropdownRoleValue);
  };

  const handleOnSelectRoleDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setDropdownRoleValue(option.value),
    [],
  );

  useEffect(() => {
    setDropdownRoleValue(undefined);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      title={t('attach_entity', { entity: t(entityType) })}
      className="h-fit w-128"
      handleClose={onHandleClose}
    >
      <div className="mt-6 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          {children}
          <div className="text-sm font-bold capitalize">{t('role')}</div>
          <Dropdown
            className="h-10 w-full rounded-md  bg-brightGray capitalize italic"
            placeholder={t('select')}
            options={roleDropdownOptions}
            value={dropdownRoleValue}
            onSelect={handleOnSelectRoleDropdownValue}
          />
        </div>
        <div className="mt-8 flex justify-between">
          <Button
            isDisabled={isSubmitting}
            onClick={onHandleClose}
            variant="outline"
            className="h-9 w-24"
          >
            {t('cancel')}
          </Button>
          <Button
            isDisabled={isSubmitting || isConfirmDisabled || !dropdownRoleValue}
            onClick={handleConfirm}
            className="h-9 w-24"
          >
            {t('create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
